import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";

import { requestPasswordReset } from "../services/authService";
import { errorMessageKey, normaliseError } from "../../../lib/apiError";

type FormValues = { email: string };

export default function ForgotPasswordPage() {
  const { t } = useTranslation(["auth", "errors"]);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: { email: "" } });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await requestPasswordReset(values.email.trim());
      // Always the same confirmation. The endpoint answers 202 regardless, so
      // the UI must not become the account-enumeration oracle the API refuses
      // to be.
      setSent(true);
    } catch (error) {
      const normalised = normaliseError(error);
      // 429 and validation errors are real answers worth showing; anything
      // else still shows the neutral confirmation.
      if (normalised.status === 429 || normalised.status === 400) {
        setFormError(t(errorMessageKey(normalised)));
      } else {
        setSent(true);
      }
    }
  });

  return (
    <Card elevation={3} sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack spacing={3} component="form" onSubmit={onSubmit} noValidate>
          <Typography variant="h2" component="h1">
            {t("auth:forgot.title")}
          </Typography>

          {sent ? (
            <Alert severity="success">{t("auth:forgot.sent")}</Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {t("auth:forgot.intro")}
              </Typography>

              {formError ? <Alert severity="error">{formError}</Alert> : null}

              <TextField
                {...register("email", {
                  required: t("auth:validation.emailRequired"),
                  pattern: {
                    value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
                    message: t("auth:validation.emailInvalid"),
                  },
                })}
                label={t("auth:signIn.email")}
                type="email"
                autoComplete="username"
                autoFocus
                fullWidth
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
              />

              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{ borderRadius: 8, px: 5 }}
                >
                  {t("auth:forgot.submit")}
                </Button>
              </Box>
            </>
          )}

          <MuiLink component={Link} to="/signin" variant="body2">
            {t("auth:forgot.backToSignIn")}
          </MuiLink>
        </Stack>
      </CardContent>
    </Card>
  );
}
